// Inline-clarification resolve ceremony — carved from
// `src/features/tool-approvals/hooks/useResolveApproval.ts` (ARCFE-1079 · D4,
// the `agents/harness` mutation slice). The transport already moved at
// ARCFE-1059 (`data/api/tool-approvals.ts`); this is the hook.
//
// HEADLESS per packages/CLAUDE.md. Three imports could not come as they stood
// and each was resolved differently, deliberately:
//
//   `@/features/inbox`            -> `../../contracts/keys`. A specifier swap:
//                                    that path re-exports THIS package's frozen
//                                    `inboxQueryKeys`, so the invalidation
//                                    targets a byte-identical tuple.
//   `@/utils/logger/logger`       -> `@heidi/runtime/logger`, already the
//                                    logger in this zone's agent-memory
//                                    mutations.
//   `toast` + `useIntl`           -> the `onResolveError` PORT. A user-visible
//                                    effect is an app reaction; the
//                                    delegate fires the same toast with the same
//                                    `intl.formatMessage` ids, at exactly the
//                                    point `toast(...)` fired here.
//
// `resolveApproval` IS A PARAMETER, and that is compliance rather than
// ceremony. Four consumer suites intercept this call, and a relative
// `../api/tool-approvals` import here would be unreachable from the app: this
// package's door list is exactly three and asserted by EQUALITY, so there is no
// deep path to mock. Taking the fn as an argument is also the shape this family
// already ratified for `transport: RunTransport` on the four run mutations, for
// the same reason: the app owns assembly.
//
// WHERE THE FOUR INTERCEPT MOVED. They used to name
// `@/features/tool-approvals/api`, and reaching past that alias was measured at
// 23 failures in `InlineClarificationBlock.test.tsx`. The alias is retired;
// they now name `@heidi/agents/data` and override `resolveToolApproval` on it,
// each spreading `jest.requireActual` first because the door is a barrel. The
// interception point is still load-bearing and still measured: dropping the
// override from `InlineClarificationBlock.test.tsx` fails all 39 of its tests.
//
// The LOCKING is data behaviour and stays: `isLockedRef` latches synchronously
// so a second invoke in the same tick cannot slip through before React
// re-renders, and an unaccepted response unlocks so the user can retry.
import { logger, sanitizeError } from '@heidi/runtime/logger';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';

import { inboxQueryKeys } from '../../contracts/keys';
import type {
  PendingToolApproval,
  ResolveToolApprovalRequest,
  ResolveToolApprovalResponse,
} from '../../contracts/tool-approval.types';

export type ApprovalVerdict = 'approve' | 'deny';

type ModifiedInput = Record<string, unknown> | undefined;

export type ResolveToolApprovalFn = (
  approval: Pick<PendingToolApproval, 'agentRunId' | 'approvalId'>,
  body: ResolveToolApprovalRequest
) => Promise<ResolveToolApprovalResponse>;

export interface UseResolveApprovalOptions {
  /**
   * The resolve transport. `resolveToolApproval` from this same `/data` door is
   * the only production implementation; it is passed in rather than imported so
   * the app path stays the interception point (see the header).
   */
  resolveApproval: ResolveToolApprovalFn;
  /** Prefix for resolve-failure logs (e.g. ``[inline-clarification]``). */
  logTag?: string;
  /**
   * App-reaction port: fired exactly where the destructive toast fired. The
   * copy and its `intl` ids live app-side, in the old-path delegate.
   */
  onResolveError?: (details: {
    approvalId: string;
    agentRunId: string;
    verdict: ApprovalVerdict;
  }) => void;
}

/**
 * Shared harness resolve ceremony for inline clarification cards:
 * latch verdict → ``resolveToolApproval`` → inbox invalidate → error port.
 * Callers supply only their ``modified_input`` builder.
 */
export function useResolveApproval(
  approval: Pick<PendingToolApproval, 'agentRunId' | 'approvalId'>,
  options: UseResolveApprovalOptions
) {
  const queryClient = useQueryClient();
  const { resolveApproval } = options;
  const logTag = options.logTag ?? '[tool-approval]';
  const onResolveError = options.onResolveError;

  const [pendingVerdict, setPendingVerdict] = useState<ApprovalVerdict | null>(null);
  const [isResolving, setIsResolving] = useState(false);

  const isLockedRef = useRef(false);

  const reset = useCallback(() => {
    isLockedRef.current = false;
    setPendingVerdict(null);
    setIsResolving(false);
  }, []);

  const resolve = useCallback(
    async (
      verdict: ApprovalVerdict,
      getModifiedInput?: (verdict: ApprovalVerdict) => ModifiedInput
    ): Promise<void> => {
      if (isLockedRef.current) return;

      const modified = getModifiedInput?.(verdict);

      // Latch synchronously so a second invoke in the same tick cannot
      // slip through before React re-renders with ``pendingVerdict``.
      isLockedRef.current = true;
      setPendingVerdict(verdict);
      setIsResolving(true);
      try {
        const response = await resolveApproval(approval, {
          verdict,
          reason: '',
          modified_input: modified,
        });
        if (!response.accepted) {
          setPendingVerdict(null);
          isLockedRef.current = false;
          return;
        }
        void queryClient.invalidateQueries({ queryKey: inboxQueryKeys.all });
      } catch (err) {
        // A fixed message and the sanitised error: never the error's text.
        logger.error(`${logTag} resolve failed`, {
          approval_id: approval.approvalId,
          agent_run_id: approval.agentRunId,
          verdict,
          error: sanitizeError(err),
        });
        onResolveError?.({
          approvalId: approval.approvalId,
          agentRunId: approval.agentRunId,
          verdict,
        });
        setPendingVerdict(null);
        isLockedRef.current = false;
      } finally {
        setIsResolving(false);
      }
    },
    [approval, logTag, onResolveError, queryClient, resolveApproval]
  );

  const isLocked = isLockedRef.current || isResolving || pendingVerdict !== null;

  return { resolve, pendingVerdict, isResolving, isLocked, reset };
}
